import {
  getHourTickIntervals,
  ITickInterval,
  getDayTickIntervals,
  getLocalTimeDayTickIntervals,
} from "../TimelineUtils";
import { Camera } from "../Camera";
import { TimelineThemeGL } from "../TimelineThemeGL";
import { BaseTimelineGL } from "../BaseTimelineGL";
import { IBaseTime } from "src/redux/reducers/CrewScheduleReducer";
import { IBiddingNFACounts } from "src/redux/actions/Bidding.Actions";
import moment from "moment-timezone";

const sixHoursAsMillis = 2.16e7;
const twoDaysAsMillis = 1.728e8;

const majorTimeStart = BaseTimelineGL.timelineStart - 40;
const minorTimeStart = majorTimeStart + 20;

export const drawTimeAxisCanvas = (
  textCanvasCtx: CanvasRenderingContext2D,
  camera: Camera,
  theme: TimelineThemeGL,
  baseTime: IBaseTime,
  biddingNFACounts?: IBiddingNFACounts // optional biddingNFA Counts if passed in.
): void => {
  const ratio = window.devicePixelRatio;
  const [xMin, xMax] = camera.getXRange();
  const [, xMaxPixels] = [0, textCanvasCtx.canvas.width / ratio];
  const approxTickIntervalMillis = (60 * (xMax - xMin)) / xMaxPixels;
  const fontSize = 14 * ratio;
  let tickTimes: ITickInterval[] = [];
  let detailTickTimes: ITickInterval[] = [];

  let isHourInterval = false;
  const isDayInterval = approxTickIntervalMillis > sixHoursAsMillis && approxTickIntervalMillis < twoDaysAsMillis;
  if (approxTickIntervalMillis > sixHoursAsMillis) {
    [tickTimes, detailTickTimes] = baseTime.isUTC
      ? getDayTickIntervals(xMin, xMax, approxTickIntervalMillis, baseTime)
      : getLocalTimeDayTickIntervals(xMin, xMax, approxTickIntervalMillis, baseTime);
  } else {
    [tickTimes, detailTickTimes] = getHourTickIntervals(xMin, xMax, approxTickIntervalMillis, baseTime);
    isHourInterval = true;
  }

  textCanvasCtx.save();

  textCanvasCtx.font = `normal normal ${fontSize}px Heebo`;
  textCanvasCtx.fillStyle = theme.minorTimeColor;
  textCanvasCtx.textAlign = "center";
  const drawNFADescription: string[] = [];
  tickTimes.forEach((timeTick: ITickInterval, index: number) => {
    const start = camera.timeAxisToPixelCoords(timeTick.timeMillis, textCanvasCtx.canvas.width);
    let end =
      index + 1 < tickTimes.length
        ? camera.timeAxisToPixelCoords(tickTimes[index + 1].timeMillis, textCanvasCtx.canvas.width)
        : start;
    textCanvasCtx.fillStyle = theme.majorTimeColor;
    textCanvasCtx.fillText(timeTick.label, start, minorTimeStart * ratio);
    textCanvasCtx.fillRect(start - 1, (minorTimeStart + 8) * ratio, 2 * ratio, 6 * ratio);

    if (isDayInterval && start < end && timeTick.dayStr) {
      const mid = (start + end) / 2;
      const fontSize = Math.min(18 * ratio, (end - start) / 3);

      // Calculate vertical position for centering
      const textHeight = fontSize * 0.7;
      const verticalPosition = minorTimeStart * ratio + textHeight / 2;

      textCanvasCtx.save();
      textCanvasCtx.font = `normal bold ${fontSize}px Heebo`;
      textCanvasCtx.fillStyle = "#5D6E81";
      textCanvasCtx.fillText(timeTick.dayStr, mid, verticalPosition);
      textCanvasCtx.restore();
    }

    if ((isDayInterval || isHourInterval) && start < end && timeTick.dayStr && biddingNFACounts) {
      const date = moment(timeTick.timeMillis).utc().format("YYYY/MM/DD");
      const biddingInfo = biddingNFACounts?.[date];
      if (biddingInfo) {
        if (isHourInterval) {
          //adjust the end time to be the end of the day or the last tick time
          end = camera.timeAxisToPixelCoords(
            tickTimes[Math.min(index + timeTick.dayIndexes, tickTimes.length - 1)].timeMillis,
            textCanvasCtx.canvas.width
          );
        }
        Object.values(biddingInfo)?.forEach((selectedNFA, nfaIndex) => {
          //console.log("biddingInfo", biddingInfo);
          const mid = (start + end) / 2;
          const fontSize = Math.min(14 * ratio, end - start);
          const devicePixelRatio = window.devicePixelRatio || 1;
          const nfaCountY0 =
            (BaseTimelineGL.timelineStart -
              5 +
              BaseTimelineGL.biddingWindowBarHeight +
              BaseTimelineGL.NFAcountRowHeight * nfaIndex) *
            devicePixelRatio;
          const nfaBarHeight = (BaseTimelineGL.NFAcountRowHeight - 4) * devicePixelRatio;

          const blockWidth = end - start - 4;
          textCanvasCtx.save();
          textCanvasCtx.font = `normal bold ${fontSize}px Heebo`; //Roboto Mono
          textCanvasCtx.fillStyle = `#${selectedNFA.color}`;
          // NFAcountRowHeight includes 2px margin buffers for top and bottom so remove those when drawing the rectangle.
          textCanvasCtx.fillRect(start + 2, nfaCountY0 + 2, blockWidth, nfaBarHeight);
          textCanvasCtx.fillStyle = "#000000";

          // Calculate vertical position for centering the text
          const textHeight = fontSize * 0.9;
          const verticalPosition = nfaCountY0 + (nfaBarHeight - textHeight) / 2 + textHeight;
          textCanvasCtx.textAlign = "center";
          textCanvasCtx.fillText(selectedNFA.count.toString(), mid, verticalPosition);

          // Make sure descriptions for NFA count rows are only drawn once
          if (!drawNFADescription.includes(selectedNFA.title)) {
            textCanvasCtx.fillStyle = "#FFFFFF";
            textCanvasCtx.textAlign = "right";
            const rowLabel = `${selectedNFA.description} (${selectedNFA.title})`;
            textCanvasCtx.fillText(rowLabel, start - 15, verticalPosition);
            drawNFADescription.push(selectedNFA.title);
          }
          textCanvasCtx.restore();
        });
      }
    }

    if (timeTick.isHighlighted) {
      textCanvasCtx.fillStyle = theme.timeHighlightBar;
      // const width = textCanvasCtx.measureText(timeTick.label).width;
      const halfWidth = 16; //width / 2.0;
      textCanvasCtx.fillRect(start - halfWidth / 2.0, 0, halfWidth, textCanvasCtx.canvas.height);
    }
  });

  if (detailTickTimes.length > 0) {
    textCanvasCtx.fillStyle = theme.minorTimeColor;
    detailTickTimes.forEach((timeTick: ITickInterval, index: number, arr: ITickInterval[]) => {
      const start = index > 0 ? camera.timeAxisToPixelCoords(timeTick.timeMillis, textCanvasCtx.canvas.width) : 0;
      if (index === detailTickTimes.length - 1) {
        const x = start + (textCanvasCtx.canvas.width - start) / 2.0;
        textCanvasCtx.fillText(timeTick.label, x, majorTimeStart * ratio);
      } else {
        const end = camera.timeAxisToPixelCoords(arr[index + 1].timeMillis, textCanvasCtx.canvas.width);
        const x = start + (end - start) / 2.0;
        textCanvasCtx.fillText(timeTick.label, x, majorTimeStart * ratio);
      }
    });
  }

  textCanvasCtx.restore();
};
